<template>
  <UiModalOrBottomSheet
    :name="props.name"
    :size="modalSize"
    :persistent="props.persistent"
    :model-value="props.modelValue"
    :class-list="[
      $style['v-modal'],
      (modalSize === 'full') && $style['v-modal_full'],
    ]"
    :style-list="{
      '--bg': backgroundColor,
    }"
    :z-index="props.zIndex"
    :title="props.title || props.popupTitle"
    @closed="onClose"
    @opened="onOpen"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div v-if="!isMobile || props.persistent" :class="$style['v-modal__wrapper']">
      <div v-if="title || popupTitle" :class="[$style['v-modal__header'], isMobile && $style['v-modal__header_fixed']]">
        <h3 :class="$style['v-modal__title']" v-html="title || popupTitle" />
      </div>
      <div :class="$style['v-modal__body']">
        <div :class="[$style['v-modal__body-container'], $style['_scrollbar']]">
          <slot />
        </div>
      </div>
      <div v-if="props.action.length > 0" :class="$style['v-modal__actions']">
        <component
          v-for="(btn, index) in props.action"
          :key="index"
          :is="btn.url ? 'a' : 'button'"
          class="v-modal__action-button button button_modal"
          :class="[`${btn.class}`, { 'js-anchor': actionIsAnchor(btn) && !btn.target }]"
          :href="btn.url"
          :target="btn.target ? btn.target : false"
          :type="btn.url ? undefined : 'button'"
          v-html="btn.name"
          @click="actionOnClick(btn)"
        />
      </div>
      <div
        v-if="$slots.footer"
      >
        <slot name="footer" />
      </div>
    </div>
    <div v-else class="_pl-16 _pr-16 _pb-16">
      <slot />
    </div>
    <button
      v-if="!isMobile && !props.persistent"
      :class="$style['v-modal__close']"
      type="button"
      title="Закрыть"
      @click="$modal.hide(name)"
    >
      <img src="/local/templates/redesign/src/assets/img/icons/icon-popup-close.svg" alt="Закрыть">
    </button>
    <template v-if="$slots.footer" #footer>
      <slot name="footer" />
    </template>
    <template v-else-if="props.action.length > 0" #footer>
      <div :class="[$style['v-modal__actions'], $style['v-modal__actions_bottom-sheet']]">
        <component
          v-for="(btn, index) in props.action"
          :key="index"
          :is="btn.url ? 'a' : 'button'"
          class="v-modal__action-button button button_modal"
          :class="[`${btn.class}`, { 'js-anchor': actionIsAnchor(btn) && !btn.target }]"
          :href="btn.url"
          :target="btn.target ? btn.target : false"
          :type="btn.url ? undefined : 'button'"
          v-html="btn.name"
          @click="actionOnClick(btn)"
        />
      </div>
    </template>
  </UiModalOrBottomSheet>
</template>
<script setup>
import { computed } from 'vue';
import { useModalStore } from '../store/useModalStore';
import { useAppState } from '../store/useAppState';
import { useId } from '../hooks/useId';
import UiModalOrBottomSheet from '../components-manual/ui/UiModalOrBottomSheet.vue';

defineOptions({
  name: 'vModal',
});

const props = defineProps({
  title: {
    type: String
  },
  /** @deprecated */
  popupTitle: {
    type: String
  },
  name: {
    type: String,
    default: () => useId(),
  },
  /** @type {'big'|'small'|'full'} */
  size: {
    type: String,
    default: 'big',
  },
  /**
   * A number, or a string containing a number.
   * @typedef {Object} Action
   * @property {string} class - classname
   * @property {string} target - link target
   * @property {string} url - link href
   * @property {string} name - btn text | innerHtml
   * @property {boolean} close - close on click
   */
  /**
   * @type {Action[]}
   */
  action: {
    type: Array,
    default: () => {
      return []
    }
  },
  modelValue: Boolean,
  dark: Boolean,
  zIndex: {
    type: Number,
    default: 6,
  },
  /** @deprecated */
  type: String,
  id: String,
  persistent: Boolean,
  bgColor: String
});

const emit = defineEmits(['close', 'open', 'update:modelValue']);


const legalSizes = {
  extra: 'extra',
  big: 'big',
  small: 'small',
  full: 'full',
};

const defaultSize = legalSizes.big;

const mapDeprecatedSizes = {
  actions: 'small',
  footer: 'small',
  contacts: 'small',
  six: 'small',
  sms: 'small',
};

const modalSize = computed(() => props.persistent && isMobile.value ? 'full' : mapDeprecatedSizes[props.size] ?? legalSizes[props.size] ?? defaultSize);

const { isMobile } = useAppState();

const modalStore = useModalStore();

const onOpen = () => {
  emit('open');
};

const onClose = () => {
  emit('close');
};

const backgroundColor = computed(() => {
  return props.dark ? '#252628' : props.bgColor ? props.bgColor : undefined;
});

/**
 * @param {Action} btn 
 */
const actionIsAnchor = (btn) => {
  return btn.url?.startsWith('#');
};

/**
 * @param {Action} btn 
 */
const actionOnClick = (btn) => {
  if (btn.close) modalStore.closeModal(props.name);
};
</script>

<style lang="scss" module>
@import '../assets/scss/base/_variables.scss';
@import '../assets/scss/base/_mixins.scss';
@import '../assets/scss/base/_functions.scss';

._scrollbar {
  --_scrollbar-color: #{$grey-166};
  --_scrollbar-track-color: #{$grey-109};
  @include fluid('--_scrollbar-width', 4px);

  &:hover {
    --_scrollbar-color: #{$grey-166};
    --_scrollbar-track-color: #{$grey-109};
    @include fluid('--_scrollbar-width', 4px);
  }

  /** modern */
  scrollbar-width: var(--_scrollbar-width);

  &::-webkit-scrollbar  {
    width: var(--_scrollbar-width);
    height: var(--_scrollbar-width);
  }

  &::-webkit-scrollbar-track {
    background-color: var(--_scrollbar-track-color);
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--_scrollbar-color);
    border: none;
    border-radius: 99px;
  }
}

.v-modal {
  --bg: #{$white-101};
  --flex-size: 1;
  @include fluid('--wrapper-radius', 56px, 0px);
  @include fluid('--close-right', -48px);
  --close-top: 0px;

  &_full {
    --wrapper-radius: 0px;
    @include fluid('--close-right', 48px);
    @include fluid('--close-top', 48px);
  }

  &__close {
    user-select: none;
    position: absolute;
    top: var(--close-top);
    right: var(--close-right);
    background: $grey-109;
    @include fluid('width', 32px);
    @include fluid('height', 32px);
    @include fluid('border-radius', 16px);
    @include fluid('padding', 8px);
    transition: background .2s;
    cursor: pointer;

    &:hover {
      background: $white-101;
    }
  }

  &__wrapper {
    position: relative;
    background: var(--bg);
    border-radius: var(--wrapper-radius);
    @include fluid('padding-top', 56px, 16px);
    @include fluid('padding-bottom', 56px, 16px);
    display: flex;
    flex-direction: column;
    min-height: 1px;
    flex: var(--flex-size);
  }

  &__header {
    @include fluid('padding-left', 56px, 16px);
    @include fluid('padding-right', 56px, 16px);
    @include fluid('padding-bottom', 16px);
    background: var(--bg);

    &_fixed {
      position: sticky;
      z-index: 2;
      top: 0px;
    }
  }

  &__title {
    @include h2;
    color: $text-base;
  }

  &__body {
    @include fluid('padding-left', 12px);
    @include fluid('padding-right', 12px);
    display: flex;
    flex-direction: column;
    min-height: 1px;
    flex: var(--flex-size);
  }

  &__body-container {
    @include fluid('padding-left', 44px, 4px);
    @include fluid('padding-right', 44px, 4px);
    overflow-x: hidden;
    overflow-y: auto;
    flex: var(--flex-size);
  }

  &__actions {
    @include fluid('padding-left', 56px, 16px);
    @include fluid('padding-right', 56px, 16px);
    @include fluid('padding-top', 16px);
    z-index: 2;
    background: var(--bg);
    width: 100%;
    display: flex;
    @include fluid('gap', 16px);

    &_bottom-sheet {
      @include fluid('padding-bottom', 16px);
    }
  }
}

[class^="_d-bottom-sheet__title"] {
    @include mobile {
      -webkit-line-clamp: 3;
    }
  }
</style>
